import React, { useState, useEffect, useCallback } from 'react'
import { api, formatCurrency, formatDateTime } from '../../utils/api'
import MarketPriceLookupModal from './MarketPriceLookupModal'
import EngineReadBlock from '../deals/quote-assessment/EngineReadBlock'

// Market sell-price (SP) section for the Edit-price modal (Slice 2). Reference
// only — renders the looked-up market estimate (read-only) + notes + lookup
// history. It NEVER touches the D2C price field above it; the operator reads it
// and decides. A lookup is cap- + budget-gated in Prism (one per product/month).

function eventLabel(type) {
  return { lookup: 'Lookup', manual_edit: 'Manual edit', note: 'Note' }[type] || type
}

// The single lookup runs as a one-item job, and the drain absorbs a per-item
// worker failure into a 'completed' job (errors don't fail the job). So a failed
// or skipped lookup would otherwise be indistinguishable from success — map the
// terminal job status to an operator-facing message (null = success). `final` is
// the last polled status dict, or null if polling never saw a terminal state.
// Exported for unit testing.
export function jobOutcomeMessage(final, jobId) {
  if (final?.errors) return 'Market-price lookup failed — please try again.'
  if (final?.status === 'budget_exhausted') return 'Lookup skipped — the monthly market-price budget is spent.'
  if (final && final.done === 0 && final.skipped > 0) return 'Lookup skipped — already looked up this month.'
  if (jobId && !final) return 'Lookup is taking longer than usual — refresh in a moment.'
  return null
}

// `embedded`: rendered inside a parent "Market pricing" section (EditPriceModal)
// — smaller sub-header, no divider of its own. Default keeps the standalone
// look used by ProductDetailModal.
export default function MarketPriceSection({ productId, title, embedded = false }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [noteInput, setNoteInput] = useState('')
  const [reviewOpen, setReviewOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const d = await api.get(`/api/pricing/market-price/${productId}`)
      setData(d)
      setNoteInput(d.notes || '')
    } catch (e) {
      setError(e.message || 'Failed to load market price')
    } finally {
      setLoading(false)
    }
  }, [productId])

  useEffect(() => { load() }, [load])

  async function runLookup(searchTerm) {
    setReviewOpen(false)
    setError(null)
    setBusy(true)
    try {
      // Fire the review'd lookup as a one-item job (reuses the bulk transport)
      // so the request returns immediately — the slow worker call runs in Prism's
      // drain, and we poll. This is what removes the 504 on the old inline lookup.
      const job = await api.post('/api/pricing/market-price/bulk-lookup', {
        shopify_product_ids: [productId],
        search_term: searchTerm,
      })
      const jobId = job?.job_id
      let final = null
      for (let i = 0; jobId && i < 60; i++) {   // poll up to ~3 min, then give up
        await new Promise(r => setTimeout(r, 3000))
        try {
          const s = await api.get(`/api/pricing/market-price/bulk-lookup/${jobId}`)
          if (s?.status === 'completed' || s?.status === 'budget_exhausted') { final = s; break }
        } catch {
          // A transient poll failure must not abort a healthy job — keep waiting (M2).
        }
      }
      await load()
      const msg = jobOutcomeMessage(final, jobId)   // surface failures/skips the drain hid (H1)
      if (msg) setError(msg)
    } catch (e) {
      setError(e.message || 'Lookup failed')
    } finally {
      setBusy(false)
    }
  }

  async function handleSaveNote() {
    setError(null)
    setBusy(true)
    try {
      await api.put(`/api/pricing/market-price/${productId}`, { notes: noteInput })
      await load()
    } catch (e) {
      setError(e.message || 'Save failed')
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <div style={{ color: '#6b7280', fontSize: 12, padding: '8px 0' }}>Loading market price…</div>

  const canLookup = data?.can_lookup && !busy
  const lookupHint = data?.next_refresh_at
    ? `Next refresh ${formatDateTime(data.next_refresh_at)}`
    : (data?.can_lookup ? '' : 'Lookups unavailable (budget/disabled)')
  const lookupLabel = busy
    ? 'Working…'
    : data?.can_lookup
      ? 'Look up market price'
      : data?.next_refresh_at
        ? 'Looked up this month'
        : 'Lookups unavailable'

  return (
    <div style={embedded ? {} : { borderTop: '1px solid var(--border-color)', marginTop: 12, paddingTop: 12 }}>
      {embedded ? (
        <div style={{ fontWeight: 600, fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
          Market price lookup <span style={{ color: '#9ca3af', fontWeight: 400 }}>(reference — does not change the price above)</span>
        </div>
      ) : (
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
          Market price <span style={{ color: '#9ca3af', fontWeight: 400 }}>(reference — does not change the price above)</span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ fontSize: 18, fontWeight: 600 }}>
          {data?.sp_cents != null ? formatCurrency(data.sp_cents / 100) : '—'}
        </span>
        <button
          type="button"
          className={`btn btn-sm ${canLookup ? 'btn-primary' : 'btn-secondary'}`}
          disabled={!canLookup} onClick={() => setReviewOpen(true)}
          title={lookupHint}
          style={!canLookup ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
        >
          {lookupLabel}
        </button>
        {lookupHint && <span style={{ color: '#9ca3af', fontSize: 10, alignSelf: 'center' }}>{lookupHint}</span>}
      </div>

      {busy && (
        <div style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic', marginBottom: 8 }}>
          Searching Google Shopping — this can take a minute or two…
        </div>
      )}

      <div style={{ color: '#6b7280', fontSize: 11, marginBottom: 8 }}>
        {data?.sp_cents != null
          ? <>source {data.sp_source} · confidence {data.confidence != null ? `${Math.round(data.confidence * 100)}%` : '—'} · {data.comparable_count ?? '—'} comparables{data.search_term ? ` · “${data.search_term}”` : ''}</>
          : 'No market price yet — click “Look up market price”.'}
      </div>

      {/* How the item sells, from the latest lookup. Shown without a price too: a
          lookup that found none still reports its demand read. */}
      <EngineReadBlock
        signals={{
          demand_tier: data?.demand_tier,
          sale_probability: data?.sale_probability,
          market_state: data?.market_state,
          volatility_pct: data?.volatility_pct,
        }}
        counts={{ sold: data?.sold_count ?? null, active: data?.active_count ?? null }}
        showZeroCounts
      />

      <div className="form-group" style={{ marginBottom: 8 }}>
        <label style={{ fontSize: 11 }}>Notes</label>
        <textarea rows={2} value={noteInput} onChange={e => setNoteInput(e.target.value)} style={{ width: '100%', resize: 'vertical' }} />
        <button type="button" className="btn btn-sm btn-secondary" disabled={busy} onClick={handleSaveNote} style={{ marginTop: 4 }}>Save note</button>
      </div>

      {error && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>{error}</div>}

      {data?.history?.length > 0 && (
        <details>
          <summary style={{ cursor: 'pointer', fontSize: 11, color: '#6b7280' }}>History ({data.history.length})</summary>
          <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 6 }}>
            {data.history.map((e, i) => (
              <div key={i} style={{ fontSize: 11, color: '#6b7280', padding: '3px 0', borderBottom: '1px solid var(--border-color)' }}>
                <span style={{ fontWeight: 500 }}>{eventLabel(e.event_type)}</span>
                {e.sp_cents != null && <> · {formatCurrency(e.sp_cents / 100)}</>}
                {e.note && <> · “{e.note}”</>}
                <span style={{ color: '#9ca3af' }}> · {e.actor} · {formatDateTime(e.created_at)}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {reviewOpen && (
        <MarketPriceLookupModal
          title={title}
          initialTerm={data?.search_term || null}
          onRun={runLookup}
          onClose={() => setReviewOpen(false)}
        />
      )}
    </div>
  )
}
